import {
  Format,
  type PaymentType,
  type PredefinedValidation,
  PriceType,
  type Product,
  type Validation,
  ValidationFormat,
  type ValidationObjectArrayType,
} from '@wix/ambassador-forms-v4-form/types';
import { type SomeJSONSchema } from 'ajv/dist/types/json-schema';
import { propertySchemaMapper } from './property-schema-mapper';
import { PropertyTypes } from './property-types';
import { asRequired } from '../../utils/lang';
import { getArrayValidationProperties } from './array-schema-property-mapper';
import { ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';

export function mapToPredefinedSchemaProperty(
  target: String | undefined,
  predefinedType: PredefinedValidation,
  validationMode: ValidationMode = ValidationMode.UNKNOWN,
): Omit<SomeJSONSchema, '$id'> | undefined {
  const { format: predefinedTypeFormat } = predefinedType;
  switch (predefinedTypeFormat) {
    case ValidationFormat.WIX_FILE:
      return propertySchemaMapper(
        PREDEFINED_VALIDATIONS[ValidationFormat.WIX_FILE],
        validationMode,
      );
    case ValidationFormat.SCHEDULING:
      return propertySchemaMapper(
        PREDEFINED_VALIDATIONS[ValidationFormat.SCHEDULING],
        validationMode,
      );
    case ValidationFormat.PAYMENT:
      return buildPaymentValidation(
        predefinedType.paymentOptions as PaymentType,
        validationMode,
      );
    case ValidationFormat.OBJECT_ARRAY:
      return buildObjectArrayValidation(
        asRequired(target, {}, 'target'),
        predefinedType.objectArrayOptions ?? {},
        validationMode,
      );
    case ValidationFormat.MULTILINE_ADDRESS:
      throw new Error(`Multiline address field is handled as referenced field`);
    default:
      throw new Error(
        `Unknown predefined validation type ${predefinedTypeFormat}`,
      );
  }
}

function buildObjectArrayValidation(
  target: String,
  objectArrayOptions: ValidationObjectArrayType,
  validationMode: ValidationMode,
): Omit<SomeJSONSchema, '$id'> {
  return {
    type: PropertyTypes.ARRAY,
    ...(objectArrayOptions.maxItems &&
      validationMode !== ValidationMode.UNRESTRICTED_UPDATE && {
        maxItems: objectArrayOptions.maxItems,
      }),
    ...(objectArrayOptions.minItems &&
      validationMode !== ValidationMode.UNRESTRICTED_UPDATE && {
        minItems: objectArrayOptions.minItems,
      }),
    items: {
      $ref: target,
    },
  };
}

function buildPaymentValidation(
  paymentOptions: PaymentType,
  validationMode: ValidationMode,
): Omit<SomeJSONSchema, '$id'> {
  if (validationMode === ValidationMode.UNRESTRICTED_UPDATE) {
    return {
      type: PropertyTypes.ARRAY,
      items: {
        type: PropertyTypes.OBJECT,
        properties: {
          productId: { type: PropertyTypes.STRING },
          price: { type: PropertyTypes.STRING },
          quantity: { type: PropertyTypes.INTEGER },
          name: { type: PropertyTypes.STRING },
        },
        uniqueItemProperties: ['productId'],
        required: ['productId', 'price', 'quantity'],
      },
    };
  }

  const products = paymentOptions.products || [];
  const { minItems, maxItems } = paymentOptions;
  const anyOfProduct = products.map((product) => {
    const { minimum, maximum } = product.quantityLimit || {};
    return propertySchemaMapper(
      {
        object: {
          properties: {
            productId: {
              string: { enum: [asRequired(product.id, product, 'id')] },
              required: true,
            },
            price: { ...buildPriceValidation(product), required: true },
            quantity: {
              integer: {
                minimum: minimum ?? 1,
                maximum: maximum ?? 100000,
              },
              required: true,
            },
            name: {
              string: {
                maxLength: 400,
              },
            },
          },
        },
      },
      validationMode,
    );
  });
  return {
    type: PropertyTypes.ARRAY,
    items: {
      type: PropertyTypes.OBJECT,
      anyOf: anyOfProduct,
    },
    ...getArrayValidationProperties({ minItems, maxItems }),
    uniqueItemProperties: ['productId'],
    errorMessage: {
      uniqueItemProperties: 'productId should appear in array at most once',
    },
  };
}

function buildPriceValidation(product: Product): Validation {
  const { priceType } = product;
  switch (priceType) {
    case PriceType.FIXED_PRICE: {
      const fixedPriceOptions = asRequired(
        product.fixedPriceOptions,
        product,
        'fixedPriceOptions',
      );
      const price = asRequired(
        fixedPriceOptions.price,
        fixedPriceOptions,
        'price',
      );
      return {
        string: {
          enum: [price],
        },
      };
    }
    case PriceType.DYNAMIC_PRICE: {
      const dynamicPriceOptions = asRequired(
        product.dynamicPriceOptions,
        product,
        'dynamicPriceOptions',
      );
      const minPrice = asRequired(
        dynamicPriceOptions.minPrice,
        dynamicPriceOptions,
        'minPrice',
      );
      const { maxPrice } = dynamicPriceOptions;
      return {
        number: {
          minimum: parseFloat(minPrice),
          ...(maxPrice && { maximum: parseFloat(maxPrice) }),
          multipleOf: 0.01,
        },
      };
    }
    default:
      throw new Error(`Unknown price type ${priceType}`);
  }
}

const PREDEFINED_VALIDATIONS: { [key: string]: Validation } = {
  [ValidationFormat.WIX_FILE]: {
    array: {
      maxItems: 30,
      minItems: 1,
      items: {
        object: {
          properties: {
            fileId: {
              string: {
                maxLength: 100,
              },
              required: true,
            },
            displayName: {
              string: {
                maxLength: 300,
              },
              required: true,
            },
            url: {
              string: {
                minLength: 1,
                maxLength: 300,
              },
              required: false,
            },
            fileType: {
              string: {
                maxLength: 100,
              },
              required: true,
            },
            imported: {
              boolean: {},
              required: false,
            },
          },
        },
      },
    },
  },
  [ValidationFormat.SCHEDULING]: {
    object: {
      properties: {
        startDate: {
          string: {
            format: Format.DATE_TIME,
          },
          required: true,
        },
        endDate: {
          string: {
            format: Format.DATE_TIME,
          },
          required: true,
        },
        timeZone: {
          string: {
            format: 'TIME_ZONE' as Format, // TODO edgbuc: add type to proto
            maxLength: 50,
          },
          required: true,
        },
      },
    },
  },
};
